import React, { useState, useEffect } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  useGetDashboardsQuery,
  useGetDefaultDashboardQuery,
  useSetDefaultDashboardMutation,
  useDeleteDashboardMutation,
  useCreateDashboardMutation,
} from '../services/dashboardApi';
import { CreateDashboardModal, DashboardTabBar } from '../components/dashboards';
import DashboardLayout from '../components/widgets/DashboardLayout';
import { useAuth } from '../hooks/useAuth';
import { Dashboard as DashboardType } from '../types/dashboard';

const ProjectDashboard: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const currentProjectId = useSelector((state: RootState) => state.project.projectId);
  const [activeDashboardId, setActiveDashboardId] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Use projectId from URL params or Redux store
  const activeProjectId = projectId || currentProjectId;

  // Fetch project-scoped dashboards
  const {
    data: dashboardsResponse,
    isLoading: isDashboardsLoading,
    error: dashboardsError,
    refetch: refetchDashboards,
  } = useGetDashboardsQuery(
    // Pass project context to the query
    activeProjectId ? { projectId: activeProjectId } : undefined
  );

  const dashboards = dashboardsResponse?.data || [];

  const { data: defaultDashboardResponse, isLoading: isDefaultLoading } =
    useGetDefaultDashboardQuery(user?.id || '', {
      skip: !user?.id,
    });

  // Mutations
  const [setDefaultDashboard] = useSetDefaultDashboardMutation();
  const [deleteDashboard] = useDeleteDashboardMutation();
  const [createDashboard] = useCreateDashboardMutation();

  // Set initial active dashboard
  useEffect(() => {
    if (dashboards.length > 0 && !activeDashboardId) {
      // Try to use default dashboard first
      const defaultDashboardId = defaultDashboardResponse?.data?.defaultDashboardId;

      if (defaultDashboardId && dashboards.some(d => d._id === defaultDashboardId)) {
        setActiveDashboardId(defaultDashboardId);
      } else {
        // Fallback to first personal dashboard or first dashboard
        const personalDashboard = dashboards.find(
          d => d.visibility === 'personal' && d.ownerUserId === user?.id
        );
        const fallbackDashboard = personalDashboard || dashboards[0];
        setActiveDashboardId(fallbackDashboard._id);
      }
    }
  }, [dashboards, activeDashboardId, defaultDashboardResponse, user?.id]);

  const handleTabClick = (dashboardId: string) => {
    setActiveDashboardId(dashboardId);
  };

  const handleAddDashboard = () => {
    setIsCreateModalOpen(true);
  };

  const handleCreateSuccess = (dashboard: DashboardType) => {
    setActiveDashboardId(dashboard._id);
    // Refetch dashboards to get the updated list
    refetchDashboards();
  };

  const handleRenameDashboard = async (dashboardId: string, currentName: string) => {
    // This would open a rename modal/prompt - placeholder for now
    const newName = window.prompt('Enter new name:', currentName);
    if (newName && newName !== currentName) {
      // TODO: Implement rename dashboard API call
    }
  };

  const handleDuplicateDashboard = async (dashboardId: string) => {
    const sourceDashboard = dashboards.find(d => d._id === dashboardId);
    if (!sourceDashboard) return;

    try {
      const result = await createDashboard({
        name: `${sourceDashboard.name} (Copy)`,
        visibility: 'personal', // Always create personal copy
        copyFromId: dashboardId,
        projectId: activeProjectId, // Include project context
      }).unwrap();

      setActiveDashboardId(result.data._id);
    } catch (_error) {
      console.error('Failed to duplicate dashboard:', _error);
    }
  };

  const handleMakeDefault = async (dashboardId: string) => {
    if (!user?.id) return;

    try {
      await setDefaultDashboard({
        userId: user.id,
        data: { dashboardId },
      }).unwrap();
    } catch (_error) {
      console.error('Failed to set default dashboard:', _error);
    }
  };

  const handleDeleteDashboard = async (dashboardId: string) => {
    const dashboard = dashboards.find(d => d._id === dashboardId);
    if (!dashboard) return;

    const confirmMessage = `Are you sure you want to delete "${dashboard.name}"? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;

    try {
      await deleteDashboard(dashboardId).unwrap();

      // If we deleted the active dashboard, switch to another one
      if (dashboardId === activeDashboardId) {
        const remainingDashboards = dashboards.filter(d => d._id !== dashboardId);
        if (remainingDashboards.length > 0) {
          setActiveDashboardId(remainingDashboards[0]._id);
        } else {
          setActiveDashboardId('');
        }
      }
    } catch (_error) {
      console.error('Failed to delete dashboard:', _error);
    }
  };

  const handleChangeVisibility = (_dashboardId: string) => {
    // This would open a visibility change modal - placeholder for now
    // TODO: Implement visibility change modal
  };

  // Redirect if no project ID
  if (!activeProjectId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-brand-600 mx-auto mb-4" />
          <p className="text-gray-600">No project selected</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (isDashboardsLoading || isDefaultLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-brand-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading project dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state - Fall back to basic project dashboard if API not available
  if (dashboardsError) {
    return (
      <div className="py-6 px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Project Dashboard</h1>
          <button
            onClick={handleAddDashboard}
            className="flex items-center space-x-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
          >
            <span>Add Dashboard</span>
          </button>
        </div>
        <DashboardLayout scope="project" projectId={activeProjectId} hideHeader />
        <CreateDashboardModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
          projectId={activeProjectId}
        />
      </div>
    );
  }

  // Empty state - no dashboards
  if (dashboards.length === 0) {
    return (
      <div className="py-6 px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Project Dashboard</h1>
          <button
            onClick={handleAddDashboard}
            className="flex items-center space-x-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
          >
            <span>Create Dashboard</span>
          </button>
        </div>

        <div className="flex items-center justify-center h-96 border-2 border-dashed border-gray-300 rounded-lg bg-white">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No project dashboards yet</h3>
            <p className="text-sm text-gray-600 mb-6 max-w-md">
              Create your first project dashboard to track project-specific data with customizable
              widgets.
            </p>
            <button
              onClick={handleAddDashboard}
              className="px-6 py-2.5 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
            >
              Create Project Dashboard
            </button>
          </div>
        </div>

        <CreateDashboardModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
          projectId={activeProjectId}
        />
      </div>
    );
  }

  // Find active dashboard
  const activeDashboard = dashboards.find(d => d._id === activeDashboardId);

  return (
    <div className="h-full flex flex-col">
      {/* Tab Bar */}
      <DashboardTabBar
        dashboards={dashboards}
        activeDashboardId={activeDashboardId}
        onTabClick={handleTabClick}
        onAddDashboard={handleAddDashboard}
        onRenameDashboard={handleRenameDashboard}
        onDuplicateDashboard={handleDuplicateDashboard}
        onMakeDefault={handleMakeDefault}
        onDeleteDashboard={handleDeleteDashboard}
        onChangeVisibility={handleChangeVisibility}
      />

      {/* Dashboard Content */}
      <div className="flex-1 py-6 px-6 lg:px-8">
        {activeDashboard ? (
          <DashboardLayout
            scope="project"
            projectId={activeProjectId}
            dashboardId={activeDashboardId}
            hideHeader
          />
        ) : (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Project dashboard not found</p>
            </div>
          </div>
        )}
      </div>

      {/* Create Dashboard Modal */}
      <CreateDashboardModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
        projectId={activeProjectId}
      />
    </div>
  );
};

export default ProjectDashboard;
